/**
 * The ranking pool one rule ranks within.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Extracted from evaluate.ts, which was at the file limit. Behaviour unchanged:
 * a MIXED rule (ranking + metric conditions) ranks only among the items its
 * metric conditions accept, so `lowest + uniqueClicks >= 50` means "the worst
 * item AMONG those with >= 50 unique clicks".
 */

import type { TagOverrideContext } from "../tag-rules/override-context";
import { tagCompareThreshold, type FlowTagTotals } from "./compare-to";
import { flowTagRuleCubeKey } from "./cube-key";
import { compareMetric, flowTagMetricValue, resolveThresholdEntry } from "./metrics";
import { createRankIndex, type FlowRankIndex } from "./ranking";
import {
  isRankingCondition,
  type FlowTagCondition,
  type FlowTagItem,
  type FlowTagRule,
} from "./types";

export function metricMatches(
  item: FlowTagItem,
  cond: FlowTagCondition,
  rule: FlowTagRule,
  overrides: TagOverrideContext,
  totals?: FlowTagTotals,
): boolean {
  if (isRankingCondition(cond)) return false;
  // The cube key, NOT the raw preset: a `custom` rule keys by its own id, and a
  // preset rule shares the entry the cube builder registered for that preset.
  const actual = flowTagMetricValue(item, cond.field, flowTagRuleCubeKey(rule));
  // The winning override ENTRY decides the threshold kind: a comparison (the
  // RHS metric over its own window — NaN when that window has no data, and
  // compareMetric's finite guard then rejects) or a pinned number.
  const entry = resolveThresholdEntry(cond, overrides);
  const threshold = entry.compareTo
    ? tagCompareThreshold(item, entry.compareTo, rule, totals)
    : entry.value;
  return compareMetric(actual, cond.operator, threshold);
}

/** A mixed rule ranks only within the items its metric conditions accept. */
export function buildRuleIndex(
  items: ReadonlyArray<FlowTagItem>,
  rule: FlowTagRule,
  base: FlowRankIndex,
  overrides: TagOverrideContext,
  totals?: FlowTagTotals,
): FlowRankIndex {
  const metric = (rule.conditions ?? []).filter((c) => !isRankingCondition(c));
  const ranked = (rule.conditions ?? []).some(isRankingCondition);
  if (!ranked || metric.length === 0) return base;
  // `filtered` tells checkRanking this pool is the rule's own qualifying set,
  // so a single qualifier is a real answer to "highest among those that clear
  // the bar" — not the unrankable lone survivor the size>=2 guard protects.
  return createRankIndex(
    items.filter((it) => metric.every((c) => metricMatches(it, c, rule, overrides, totals))),
    { filtered: true },
  );
}
